/**
 * @fileOverview automatically lazily loads modules like dialog, growl, draggable, sortable on first invocation.
 * @author Ananda Masri
 */

const debug = false;



// busy module - create dummy method stubs that trigger lazy loading
window.busy = {};
for (let method of ['start', 'stop', 'reset', 'status']) {
    window.busy[method] = async function (...args) {
        import(/* webpackChunkName: "busy" */ '@aamasri/busy')
            .then(module => {
                window.busy = module;
                if (debug) console.debug('loaded busy', typeof module);
                return window.busy[method](...args);    // actual function invocation with original arguments
            })
            .catch(error => { throwLoadingError('busy', error); });
    }
}


// auth module - create dummy method stubs that trigger lazy loading
window.auth = {};
for (let method of [ 'logout', 'loginPrompt', 'checkAccess', 'popupLoginDialog', 'sha1', 'adminPasswordReset' ]) {
    window.auth[method] = function (...args) {
        import(/* webpackChunkName: "auth" */ '../js/auth').then((module) => {
            window.auth = module;
            if (debug) console.debug('loaded auth', typeof module);
            return window.auth[method](...args);    // actual function invocation with original arguments
        }).catch(error => { throwLoadingError('auth', error); });
    }
}



// dialog package - create dummy method stubs to trigger lazy loading
window.dialog = {};

// this is slightly problematic for synchronous functions because the stub is async (to lazy-load the package).
// Once the package is loaded, however, async stubs could change to a synchronous function.
// calling a synchronous function asynchronously throws an error and vice versa
for (let method of ['open', 'close', 'closeAll', 'closeLast']) {
    window.dialog[method] = async function (...args) {
        window.dialog = await import(/* webpackChunkName: "dialog" */ '@aamasri/dialog').catch(error => {
        //window.dialog = await import(/* webpackChunkName: "dialog-debug" */ '../components/dialog-debug/dialog.js').catch(error => {
                throwLoadingError('dialog', error);
            });   // note that this will overwrite the async fn stub

        if (debug) console.debug('loaded dialog', typeof module);
        return window.dialog[method](...args);     // actual function invocation with original arguments
    }
}




// popup package - create dummy method stubs to trigger lazy loading
window.popup = {};
for (let method of ['open', 'close', 'closeAll', 'closeLast']) {
    window.popup[method] = async function (...args) {
        window.popup = await import(/* webpackChunkName: "popup" */ '@aamasri/popup').catch(error => {
            throwLoadingError('popup', error);
        });

        // note that this will overwrite the async fn stub
        if (debug) console.debug('loaded popup', typeof module);
        return window.popup[method](...args);     // actual function invocation with original arguments
    }
}



// filemanager module - create dummy method stubs that trigger lazy loading
window.filemanager = {};
for (let method of ['launchFileFinder', 'insertFile', 'insertImage', 'initAjaxUpload', 'showFileContextMenu', 'openFile', 'removeFile', 'removeFileCleanup']) {
    window.filemanager[method] = async function (...args) {
        try {
            const module = await import(/* webpackChunkName: "filemanager" */ '../js/filemanager');
            window.filemanager = module;
            if (debug) console.debug('loaded filemanager', typeof module);
            return window.filemanager[method](...args);    // actual function invocation with original arguments
        } catch (error) {
            throwLoadingError('filemanager', error);
        }
    }
}



// growl package - create dummy method stubs that trigger lazy loading
window.growl = async function (...args) {
    try {
        const module = await import(/* webpackChunkName: "growl" */ '../../../node_modules/growl-js');
        if (debug) console.debug('loaded growl', typeof module.default);
        window.growl = module.default;
        return window.growl(...args);     // actual function invocation with original arguments
    } catch (error) {
        throwLoadingError('filemanager', error);
    }
}




// apply prism to any code on page
window.highlightSyntax = function () {
    import(/* webpackChunkName: "syntaxHighlighter" */ '../js/syntaxHighlighter').then(module => {
        if (debug) console.debug('loaded syntaxHighlighter', typeof module.highlightSyntax);
        module.highlightSyntax();     // actual function invocation with original arguments
    })
    .catch(error => { throwLoadingError('syntaxHighlighter', error); });
}




/** allows scripts to load packages at point of use
 * e.g., window.load('jquery-ui-slider').then(..
 *
 * @param {string|object} packageNames - the package name(s) to load
 * @returns {Promise}
 */
export const load = (packageNames) => {
    if (typeof packageNames === 'string')
        packageNames = packageNames.split(/[\s,]+/);

    const promiseList = [];
    for (const packageName of packageNames) {
        if (debug) console.debug(`loading ${packageName}`);

        switch (packageName) {
            case 'jquery-ui-slider':
                promiseList.push(import(/* webpackChunkName: "jquery-ui-widget" */ 'jquery-ui/ui/widget'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-keycode" */ 'jquery-ui/ui/keycode'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-widgets-mouse" */ 'jquery-ui/ui/widgets/mouse'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-widgets-slider" */ 'jquery-ui/ui/widgets/slider'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-themes-base-core" */ 'jquery-ui/themes/base/core.css'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-themes-base-slider" */ 'jquery-ui/themes/base/slider.css'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-themes-base-theme" */ 'jquery-ui/themes/base/theme.css'));
                break;

            case 'jquery-ui-draggable':
                promiseList.push(import(/* webpackChunkName: "jquery-ui-widget" */ 'jquery-ui/ui/widget'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-data" */ 'jquery-ui/ui/data'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-scroll-parent" */ 'jquery-ui/ui/scroll-parent'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-widgets-draggable" */ 'jquery-ui/ui/widgets/draggable'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-widgets-mouse" */ 'jquery-ui/ui/widgets/mouse'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-themes-base-core" */ 'jquery-ui/themes/base/core.css'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-themes-base-draggable" */ 'jquery-ui/themes/base/draggable.css'));
                break;

            case 'jquery-ui-droppable':
                promiseList.push(import(/* webpackChunkName: "jquery-ui-widget" */ 'jquery-ui/ui/widget'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-data" */ 'jquery-ui/ui/data'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-scroll-parent" */ 'jquery-ui/ui/scroll-parent'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-widgets-sortable" */ 'jquery-ui/ui/widgets/draggable'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-widgets-sortable" */ 'jquery-ui/ui/widgets/droppable'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-widgets-mouse" */ 'jquery-ui/ui/widgets/mouse'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-themes-base-core" */ 'jquery-ui/themes/base/core.css'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-themes-base-sortable" */ 'jquery-ui/themes/base/draggable.css'));
                break;

            case 'jquery-ui-sortable':
                promiseList.push(import(/* webpackChunkName: "jquery-ui-widget" */ 'jquery-ui/ui/widget'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-data" */ 'jquery-ui/ui/data'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-disable-selection" */ 'jquery-ui/ui/disable-selection'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-scroll-parent" */ 'jquery-ui/ui/scroll-parent'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-widgets-sortable" */ 'jquery-ui/ui/widgets/sortable'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-widgets-mouse" */ 'jquery-ui/ui/widgets/mouse'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-themes-base-core" */ 'jquery-ui/themes/base/core.css'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-themes-base-sortable" */ 'jquery-ui/themes/base/sortable.css'));
                break;

            case 'jquery-ui-effects':
                promiseList.push(import(/* webpackChunkName: "jquery-ui-effect" */ 'jquery-ui/ui/effect'));     // core effects/easing
                promiseList.push(import(/* webpackChunkName: "jquery-ui-effect-transfer" */ 'jquery-ui/ui/effects/effect-transfer'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-effect-puff" */ 'jquery-ui/ui/effects/effect-puff'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-effect-scale" */ 'jquery-ui/ui/effects/effect-scale')); // for puff effect
                promiseList.push(import(/* webpackChunkName: "jquery-ui-effect-size" */ 'jquery-ui/ui/effects/effect-size'));   // for puff effect
                promiseList.push(import(/* webpackChunkName: "jquery-ui-effect-highlight" */ 'jquery-ui/ui/effects/effect-highlight'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-effect-pulsate" */ 'jquery-ui/ui/effects/effect-pulsate'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-effect-bounce" */ 'jquery-ui/ui/effects/effect-bounce'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-effect-pulsate" */ 'jquery-ui/ui/effects/effect-clip'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-effect-pulsate" */ 'jquery-ui/ui/effects/effect-pulsate'));
                break;

            case 'jquery-ui-autocomplete':
                promiseList.push(import(/* webpackChunkName: "jquery-ui-widget" */ 'jquery-ui/ui/widget'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-position" */ 'jquery-ui/ui/position'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-keycode" */ 'jquery-ui/ui/keycode'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-unique-id" */ 'jquery-ui/ui/unique-id'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-widgets-autocomplete" */ 'jquery-ui/ui/widgets/autocomplete'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-widgets-menu" */ 'jquery-ui/ui/widgets/menu'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-themes-base-core" */ 'jquery-ui/themes/base/core.css'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-themes-base-autocomplete" */ 'jquery-ui/themes/base/autocomplete.css'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-themes-base-menu.css" */ 'jquery-ui/themes/base/menu.css'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-themes-base-theme" */ 'jquery-ui/themes/base/theme.css'));
                break;

            case 'jquery-ui-datepicker':
                promiseList.push(import(/* webpackChunkName: "jquery-ui-keycode" */ 'jquery-ui/ui/keycode'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-widgets-datepicker" */ 'jquery-ui/ui/widgets/datepicker'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-themes-base-core" */ 'jquery-ui/themes/base/core.css'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-themes-base-datepicker" */ 'jquery-ui/themes/base/datepicker.css'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-themes-base-theme" */ 'jquery-ui/themes/base/theme.css'));
                break;

            case 'jquery-ui-progressbar':
                promiseList.push(import(/* webpackChunkName: "jquery-ui-widget" */ 'jquery-ui/ui/widget'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-widgets-progressbar" */ 'jquery-ui/ui/widgets/progressbar'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-themes-base-core" */ 'jquery-ui/themes/base/core.css'));
                promiseList.push(import(/* webpackChunkName: "jquery-ui-themes-base-progressbar" */ 'jquery-ui/themes/base/progressbar.css'));
                break;

            case 'js-cookie':
                return import(/* webpackChunkName: "js-cookie" */ 'js-cookie').then(module => module.default)
                    .catch(error => { throwLoadingError('js-cookie', error); })

            case 'filemanager':
                return import(/* webpackChunkName: "filemanager" */ './filemanager')
                    .catch(error => { throwLoadingError('filemanager', error); });

            case 'image':
                return import(/* webpackChunkName: "image" */ '@aamasri/web-components/src/js/image')
                    .catch(error => { throwLoadingError('image', error); });

            case 'html-editor':
                return import(/* webpackChunkName: "html-editor" */ '../components/ckeditor5/index')
                    .catch(error => { throwLoadingError('html-editor', error); });

            case 'file-utils':
                return import(/* webpackChunkName: "file-utils" */ '@aamasri/web-components/src/js/file-utils')
                    .catch(error => { throwLoadingError('file-utils', error); });

            case 'validation':
                return import('@aamasri/web-components/src/js/validation')
                    .catch(error => { throwLoadingError('validation', error); });

            case 'dom-utils':
                return import(/* webpackChunkName: "dom-utils" */ '@aamasri/web-components/src/js/dom-utils')
                    .catch(error => { throwLoadingError('dom-utils', error); });

            case 'string-prototypes':
                return import(/* webpackChunkName: "string-prototypes" */ '@aamasri/web-components/src/js/string-prototypes')
                    .catch(error => { throwLoadingError('string-prototypes', error); });

            case 'date-utils':
                return import(/* webpackChunkName: "date-utils" */ '@aamasri/web-components/src/js/date-utils')
                    .catch(error => { throwLoadingError('date-utils', error); });

            case 'levenshtein':
                return import(/* webpackChunkName: "levenshtein" */ './levenshtein')
                    .catch(error => { throwLoadingError('levenshtein', error); });

            case 'notifications':
                return import(/* webpackChunkName: "notifications" */ './notifications')
                    .catch(error => { throwLoadingError('notifications', error); });

            case 'dialog':
                return import(/* webpackChunkName: "dialog" */ '@aamasri/dialog')
                    .catch(error => { throwLoadingError('dialog', error); });

            case 'popup':
                return import(/* webpackChunkName: "popup" */ '@aamasri/popup')
                    .catch(error => { throwLoadingError('popup', error); });

            case 'modern-browser-support':
                return import(/* webpackChunkName: "browser-support" */ './browser-support')
                    .catch(error => { throwLoadingError('browser-support', error); });

            case 'slideshow':
                return import(/* webpackChunkName: "slideshow" */ '../components/slideshow/slideshow')
                    .catch(error => { throwLoadingError('slideshow', error); });

            case 'image-resizer':
                return import(/* webpackChunkName: "image-resizer" */ '../components/image-resizer')
                    .catch(error => { throwLoadingError('image resizer', error); });

            case 'json-api':
                return import(/* webpackChunkName: "json-api" */ '@aamasri/web-components/src/js/json-api')
                    .catch(error => { throwLoadingError('json-api', error); });

            default:
                throw `loading package "${packageName}" isn't supported by fn window.load(package) - yet!`;
        }
    }

    // merge all promises into a single promise (they can load in any order)
    return Promise.all(promiseList)
        .catch(error => { throwLoadingError(packageNames, error) });
};




function throwLoadingError(packageName, error) {
    if (document.visibilityState === 'visible')
        throw `loading ${packageName} failed with: ${error.message}`;
    else
        console.error(`page unloading - ${packageName} failed to load`);    // this never happens - we always get an "Uncaught (in promise)" error
}



window.load = load;